import React, { useState, useEffect, useCallback } from "react";
import { Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Sidebar, { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_EXPANDED_WIDTH } from "./components/Sidebar";
import { NEUTRAL } from "./styles/brandTokens";
import Topbar from "./components/Topbar";
import ChatAssistant from "./pages/ChatAssistant";
import KnowledgeBase from "./pages/KnowledgeBase";
import Guides from "./pages/Guides";
import Docs from "./pages/Docs";
import CustomWorkflows from "./pages/CustomWorkflows";
import CustomWorkflowForm from "./pages/CustomWorkflowForm";
import Schedules from "./pages/Schedules";
import Customers from "./pages/Customers";
import TheoUsers from "./pages/TheoUsers";
import Integrations from "./pages/Integrations";
import ApplicationPlaybooks from "./pages/ApplicationSkills";
import AppConfiguration from "./pages/AppConfiguration";
import { AgentConfigure } from "./pages/AgentConfigure";
import TheoAutomationsMonitor from "./pages/TheoAutomationsMonitor";
import AgentPlaceholder from "./pages/AgentPlaceholder";
import TheoOverview from "./pages/theoOverview/TheoOverview";
import CreateAgent from "./pages/CreateAgent";
import Supervisor from "./pages/Supervisor";
import SupervisorTicketDetail from "./pages/SupervisorTicketDetail";
import NeoAnalytics from "./pages/NeoAnalytics";
import TheoAI from "./pages/TheoAI";
import Configuration from "./pages/Accounts";
import UserThreadObserver from "./observer/pages/UserThreadObserver";
import GroupQueue from "./components/GroupQueue";
import GlobalLoadingOverlay from "./components/GlobalLoadingOverlay";
import ProtectedObserverRoute from "./components/ProtectedObserverRoute";
import ConfigChangeLog from "./features/configAudit/ConfigChangeLog";
import CustomWorkflowsRoute from "./components/CustomWorkflowsRoute";
import QueueAnalytics from "./pages/QueueAnalytics";
import MspAnalytics from "./pages/MspAnalytics";
import RmmDeviceDetail from "./pages/RmmDeviceDetail";
import ResearchReports from "./pages/ResearchReports";
import ResearchReportView from "./pages/ResearchReportView";
import Usage from "./pages/Usage";
import Billing from "./pages/Billing";
import AuditLogs from "./pages/AuditLogs";
import Password from "./pages/Password";
import TheoDocumentation from "./pages/TheoDocumentation";
import HelpCenterGuidesHub from "./pages/helpCenter/HelpCenterGuidesHub";
import HelpCenterGuideArticle from "./pages/helpCenter/HelpCenterGuideArticle";
import ReleaseNotes from "./pages/helpCenter/ReleaseNotes";
import { getHelpCenterGuideTitleFromPath } from "./data/helpCenterGuides";
import { ConfigurationSupportProvider } from "./configurationSupport/ConfigurationSupportContext";
import ConfigurationSupportChat from "./configurationSupport/ConfigurationSupportChat";
import { useAppSelector, useAppDispatch } from "./hooks";
import { SupervisorAction } from "./actions";
// Inter Variable must load before global.css, which references it in --theo-font-sans.
import "@fontsource-variable/inter";
import "./global.css";
import { TechnicianGroup } from "types";
import { slugify } from "utils/stringUtils";
import PlatformConfiguration from "./pages/PlatformConfiguration";
import GuideFormPage from "./components/GuideFormPage";
import GuideViewPage from "./components/GuideViewPage";
import DocFormPage from "./components/DocFormPage";
import DocViewPage from "./components/DocViewPage";
import { useUserMsp } from "./hooks/useUserMsp";
import { usePsaStatus } from "./hooks/usePsaStatus";
import { useOnboardingStatus } from "./hooks/useOnboardingStatus";
import { useFeatureFlag } from "./hooks/useFeatureFlag";
import { canAccessAnalyticsAssistant as hasAnalyticsAssistantAccess } from "./utils/analyticsAssistantAccess";
import { isBillingUiVisible } from "./utils/billingAccess";
import {
  isNinjaOne,
  isAutotask,
  isSuperOps,
  isHalo,
  isConnectWise,
  getTicketScopeLabel,
} from "./utils/mspUtils";
import PsaSetupScreen from "./components/PsaSetupScreen";
import OnboardingWizard from "./components/OnboardingWizard";
import ConnectWisePodEmbed from "./pages/ConnectWisePodEmbed";
import NinjaOneOAuthCallback from "./components/NinjaOneOAuthCallback";
import M365OAuthCallback from "./components/M365OAuthCallback";
import ExportResults from "./pages/ExportResults";
import CsvExportDownload from "./pages/CsvExportDownload";
import PdfExportDownload from "./pages/PdfExportDownload";
import SupervisorApi, { clearPsaMetadataReadCache } from "./apis/supervisorApi";
import config from "./config";
import {
  identifyMixpanelUser,
  initMixpanel,
  refreshMixpanelInternalEmailExclusion,
  trackMixpanelPageView,
} from "./utils/mixpanel";
import { isSupervisorTicketChromelessPath } from "./utils/supervisorTicketPopup";
import { isServiceDeskInboxFlagEnabled } from "./utils/serviceDeskInbox";
import { isSchedulerLdEnabled } from "./utils/schedulerAccess";
import { isAllowMultipleAgentEnabled } from "./utils/multipleAgentAccess";
import { isTheoPilotEnabled } from "./utils/theoPilotAccess";
import SectionFrame from "./navigation/SectionFrame";
import WorkScopeNav from "./navigation/WorkScopeNav";
import { agentNavRoster, DEFAULT_AGENT_SLUG, findTheoAgent } from "./navigation/theoAgents";
import { useAgents } from "./hooks/useAgents";
import { LIVE_DEBUG_SLOT_ID } from "./components/LiveDebugPanel";

function isLdFlagEnabled(flags: Record<string, unknown>, ...keys: string[]): boolean {
  for (const key of keys) {
    const v = flags[key];
    if (v === true) return true;
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      if (s === "true" || s === "on" || s === "yes" || s === "1") return true;
    }
  }
  return false;
}

/** Default post-login landing: Insights → Theo Overview. */
const HOME_PATH = "/theo-overview";

function RedirectDeepResearchThread() {
  const { threadId } = useParams<{ threadId: string }>();
  const target = threadId ? `/chat-assistant/${encodeURIComponent(threadId)}` : "/chat-assistant";
  return <Navigate to={target} replace />;
}

const SupervisorQueueRoute: React.FC<{
  technicianGroupList: TechnicianGroup[];
  ninjaoneBoardList: any[];
  mspType?: string;
}> = ({ technicianGroupList, ninjaoneBoardList, mspType }) => {
  const { slug } = useParams<{ slug: string }>();
  const technicianGroupListLoaded = useAppSelector(
    (state) => Boolean((state as { supervisor?: { technicianGroupListLoaded?: boolean } }).supervisor?.technicianGroupListLoaded)
  );
  const ninjaBoardListLoaded = useAppSelector(
    (state) => Boolean((state as { supervisor?: { ninjaBoardListLoaded?: boolean } }).supervisor?.ninjaBoardListLoaded)
  );

  if (!slug) {
    return <Supervisor />;
  }

  if (!mspType) {
    return <GlobalLoadingOverlay />;
  }

  const scopeLabel = getTicketScopeLabel(mspType);
  const queueNotFound = (
    <Box sx={{ p: 3, maxWidth: 560 }}>
      <Typography variant="h6" sx={{ fontFamily: "var(--theo-font-sans)", mb: 1 }}>
        {scopeLabel} not found
      </Typography>
      <Typography color="text.secondary" sx={{ fontFamily: "var(--theo-font-sans)" }}>
        This link does not match any current {scopeLabel.toLowerCase()} name. It may have been renamed,
        or the list may still be loading. Choose a {scopeLabel.toLowerCase()} from the list to continue.
      </Typography>
    </Box>
  );

  if (isNinjaOne(mspType)) {
    if (!ninjaBoardListLoaded) {
      return <GlobalLoadingOverlay />;
    }
    const matchedBoard = (Array.isArray(ninjaoneBoardList) ? ninjaoneBoardList : []).find(
      (board: any) => slugify(String(board?.name || "")) === slug
    );
    if (!matchedBoard) {
      return queueNotFound;
    }
    return <GroupQueue boardId={matchedBoard.id} boardName={matchedBoard.name} />;
  }

  if (isAutotask(mspType) || isHalo(mspType) || isConnectWise(mspType) || isSuperOps(mspType)) {
    if (!technicianGroupListLoaded) {
      return <GlobalLoadingOverlay />;
    }
    const matchedGroup = (Array.isArray(technicianGroupList) ? technicianGroupList : []).find(
      (group: TechnicianGroup) => slugify(String(group?.name || "")) === slug
    );
    if (!matchedGroup) {
      return queueNotFound;
    }

    return (
      <GroupQueue
        techGroupName={matchedGroup.name}
        queueId={Number(matchedGroup.groupId)}
        platformId={matchedGroup.platformId}
      />
    );
  }

  return <Supervisor />;
};

const ShortTicketLinkRoute: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const location = useLocation();
  const normalizedTicketId = String(ticketId || "").trim();

  if (!normalizedTicketId) {
    return <Navigate to={HOME_PATH} replace />;
  }

  const params = new URLSearchParams(location.search);
  const prompt = String(params.get("p") || "").trim();
  const targetParams = new URLSearchParams();
  if (prompt) {
    targetParams.set("prefillPrompt", prompt);
  }

  const targetSearch = targetParams.toString();
  return (
    <Navigate
      to={`/supervisor/tickets/${encodeURIComponent(normalizedTicketId)}${targetSearch ? `?${targetSearch}` : ""}`}
      replace
    />
  );
};

const App: React.FC = () => {
  const location = useLocation();
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [ldTimeout, setLdTimeout] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLdTimeout(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);
  const [ticketScopeCategories, setTicketScopeCategories] = useState<
    Array<{ groupId: string; name: string }>
  >([]);
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAuth0();
  const accessToken = useAppSelector((state) => state.auth?.accessToken);
  const { technicianGroupList, ninjaoneBoardList } = useAppSelector(
    (state) => state.supervisor
  );
  const { userMspInfo, isLoading: userMspLoading, error: userMspError } = useUserMsp();
  const { psaStatus, isLoading: psaStatusLoading } = usePsaStatus();
  const { status: onboardingStatus, loading: onboardingLoading, refetch: refetchOnboarding } = useOnboardingStatus(userMspInfo?.msp_id || null);
  const flags = useFeatureFlag();
  const supervisorApi = React.useMemo(() => new SupervisorApi(), []);
  const showTheoAnalytics = flags["theoanalytics"] !== false;
  const canAccessAnalyticsAssistant = hasAnalyticsAssistantAccess(
    flags as Record<string, unknown>,
    userMspInfo?.role
  );
  const theoPilot = isTheoPilotEnabled(flags as Record<string, unknown>);
  const canAccessPlaybooks =
    !theoPilot && isLdFlagEnabled(flags as Record<string, unknown>, "playbooks", "skills");
  const allowMultipleAgents = isAllowMultipleAgentEnabled(flags as Record<string, unknown>);
  const canAccessSchedules = !theoPilot && isSchedulerLdEnabled(flags as Record<string, unknown>);
  const canCreateAgent = allowMultipleAgents && !theoPilot;
  const canShowBilling = isBillingUiVisible(userMspInfo);
  const serviceDeskInboxEnabled = isServiceDeskInboxFlagEnabled(
    flags as Record<string, unknown>
  );

  useEffect(() => {
    initMixpanel(config.mixpanelToken, {
      recordSessionsPercent: config.mixpanelRecordSessionsPercent,
      excludeInternalEmails: config.mixpanelExcludeInternalEmails,
      superProperties: {
        theo_client: "horizon_web",
        product_surface: "horizon_web",
      },
    });
  }, []);

  useEffect(() => {
    refreshMixpanelInternalEmailExclusion(user?.email);
    trackMixpanelPageView(location.pathname, location.search);
  }, [location.pathname, location.search, user?.email]);

  useEffect(() => {
    if (!isAuthenticated || !user?.sub) return;
    identifyMixpanelUser(user.sub, {
      email: user.email || "",
      name: user.name || user.nickname || "",
      msp_id: userMspInfo?.msp_id ?? "",
      role: userMspInfo?.role ?? "",
    });
  }, [isAuthenticated, user?.sub, user?.email, user?.name, user?.nickname, userMspInfo?.msp_id, userMspInfo?.role]);

  useEffect(() => {
    const userEmail = user?.email;
    if (isAuthenticated && accessToken && userMspInfo?.msp_type && userEmail) {
      if (isNinjaOne(userMspInfo.msp_type)) {
        dispatch(SupervisorAction.getNinjaoneBoardList());
      } else if (isAutotask(userMspInfo.msp_type)) {
        dispatch(SupervisorAction.getTechnicianGroupList(userEmail));
      } else if (isSuperOps(userMspInfo.msp_type)) {
        dispatch(SupervisorAction.getTechnicianGroupList(userEmail));
      } else if (isHalo(userMspInfo.msp_type)) {
        dispatch(SupervisorAction.getTechnicianGroupList(userEmail));
      } else if (isConnectWise(userMspInfo.msp_type)) {
        dispatch(SupervisorAction.getTechnicianGroupList(userEmail));
      }
    }
  }, [isAuthenticated, accessToken, userMspInfo?.msp_type, userMspInfo?.msp_id, user?.email, dispatch]);

  const loadTicketScopeCategories = useCallback(async () => {
    if (
      !isAuthenticated ||
      !accessToken
    ) {
      setTicketScopeCategories([]);
      return;
    }

    try {
      const response = await supervisorApi.getTicketCategoryList();
      setTicketScopeCategories(Array.isArray(response) ? response : []);
    } catch (error) {
      setTicketScopeCategories([]);
    }
  }, [isAuthenticated, accessToken, supervisorApi]);

  useEffect(() => {
    loadTicketScopeCategories();
  }, [loadTicketScopeCategories]);

  useEffect(() => {
    const handleNavigationDataUpdated = () => {
      clearPsaMetadataReadCache();
      loadTicketScopeCategories();
      if (user?.email) {
        dispatch(SupervisorAction.getTechnicianGroupList(user.email));
      }
    };

    window.addEventListener("autotask-categories-updated", handleNavigationDataUpdated);
    window.addEventListener("ticket-scope-categories-updated", handleNavigationDataUpdated);
    return () => {
      window.removeEventListener("autotask-categories-updated", handleNavigationDataUpdated);
      window.removeEventListener("ticket-scope-categories-updated", handleNavigationDataUpdated);
    };
  }, [dispatch, loadTicketScopeCategories, user?.email]);

  const toggleSidebar = () => {
    setIsSidebarExpanded(!isSidebarExpanded);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Check if we should show onboarding wizard (only for new MSPs without PSA)
  const shouldShowOnboarding =
    isAuthenticated &&
    !psaStatusLoading &&
    !onboardingLoading &&
    psaStatus !== null &&
    psaStatus.hasPsaEnabled === false &&
    onboardingStatus !== null &&
    !onboardingStatus.onboardingCompleted;

  // This is now handled by onboarding wizard
  const shouldShowPsaSetup = false;

  const isPasswordRoute = location.pathname === "/password";
  const isPublicReleaseNotesRoute = location.pathname === "/help-center/release-notes";
  const isNinjaOneOAuthCallbackRoute = location.pathname === "/ninjaone/oauth/callback";
  const isM365OAuthCallbackRoute = location.pathname === "/m365/oauth/callback";
  const ticketScopeLabel = getTicketScopeLabel(userMspInfo?.msp_type);
  // The topbar names the agent whose screen you are on, so it reads the live roster rather than a
  // fixed list -- a renamed or newly-created agent must show its own name.
  const { agents: liveAgents, defaultAgent } = useAgents(userMspInfo?.msp_id);
  const agentRoster = React.useMemo(() => agentNavRoster(liveAgents), [liveAgents]);

  const getPageTitle = (path: string) => {
    // Return "Setup" if showing onboarding or PSA setup screen
    if (shouldShowOnboarding || shouldShowPsaSetup) {
      return "Setup";
    }

    if (path.startsWith("/supervisor"))
      return path.startsWith("/supervisor/categories")
        ? "Categories"
        : `${ticketScopeLabel}s`;
    if (path.startsWith("/chat-assistant")) return "Chat";
    if (path.startsWith("/agents/new")) return "Create an Agent";
    if (path.startsWith("/agents/")) {
      return findTheoAgent(path.split("/")[2], agentRoster)?.title || "Agents";
    }
    if (path.startsWith("/theo-automations") || path.startsWith("/ai-automations")) {
      // These are the default agent's own screens, so they carry whatever it has been renamed to.
      return defaultAgent?.name || "Theo Technician Agent";
    }
    if (path.startsWith("/devices/")) return "Device";
    if (path.startsWith("/deep-research")) return "Deep Research";
    if (path.startsWith("/research-reports")) return "Deep Research Reports";
    if (path.startsWith("/report/")) return "Deep Research Report";
    if (path.startsWith("/app-configuration")) return "Integrations";
    if (path.startsWith("/platform-configuration")) return "Integrations";
    if (path.startsWith("/custom_workflows")) return "Custom Workflows";
    if (path.startsWith("/schedules")) return "Schedules";

    const guideArticleTitle = getHelpCenterGuideTitleFromPath(path);
    if (guideArticleTitle) {
      return guideArticleTitle;
    }
    if (path === "/help-center/guides") {
      return "Guides";
    }
    if (path === "/help-center/documentation") {
      return "Documentation";
    }
    if (path === "/help-center/release-notes") {
      return "Release Notes";
    }
    if (path === "/help-center") {
      return "Help Center";
    }

    const dashboardTitle =
      userMspInfo?.msp_name ? `Dashboard for ${userMspInfo.msp_name}` : "Dashboard";

    if (path === "/msp-analytics") {
      return dashboardTitle;
    }

    const pathMap: { [key: string]: string } = {
      "/": dashboardTitle,
      "/dashboard": dashboardTitle,
      "/chat-assistant": "Chat",
      "/deep-research": "Deep Research",
      "/knowledge-base": "Knowledge Base",
      "/guides": "Workflow Guides",
      "/docs": "Docs",
      "/custom_workflows": "Custom Workflows",
      "/schedules": "Schedules",
      "/accounts": "PSA Customers",
      "/theo-users": "Theo Users",
      "/integrations": "Integrations",
      "/playbooks": "Playbooks",
      "/ai-automations": defaultAgent?.name || "Theo Technician Agent",
      "/theo-automations/monitor": defaultAgent?.name || "Theo Technician Agent",
      "/theo-automations/configure": defaultAgent?.name || "Theo Technician Agent",
      "/documentation": "Documentation",
      "/help-center/documentation": "Documentation",
      "/help-center/release-notes": "Release Notes",
      "/supervisor":
        `${ticketScopeLabel}s`,
      "/queue-analytics":
        `${ticketScopeLabel} Analytics`,
      "/usage": "Usage",
      "/theo-overview": "Theo Overview",
      "/billing": "Billing",
      "/audit-logs": "Logs",
      "/neo-analytics": "Analytics",
      "/research-reports": "Deep Research Reports",
      "/neo-ai": "Theo AI",
      "/configuration": "Configuration",
      "/observer": "Observer",
      "/observer/change-log": "Change Log",
    };
    return pathMap[path] || dashboardTitle;
  };

  const ldConfigured = Boolean(config.launchdarklyClientSideId);
  const flagsLoaded = !ldConfigured || Object.keys(flags).length > 0 || ldTimeout;
  const isMspInfoLoaded = !isAuthenticated || (!userMspLoading || userMspInfo !== null || userMspError !== null);

  // Show loading state while checking PSA status and loading critical configs
  const isEmbedRoute = location.pathname.startsWith("/embed");

  // Keep the one-time-secret page outside authenticated app loading states.
  // Otherwise those states can unmount and remount Password after it has
  // already marked the secret as viewed.
  if (isPasswordRoute) {
    return <Password />;
  }

  if (isPublicReleaseNotesRoute) {
    return <ReleaseNotes />;
  }

  if (isNinjaOneOAuthCallbackRoute) {
    return <NinjaOneOAuthCallback />;
  }

  if (isM365OAuthCallbackRoute) {
    return <M365OAuthCallback />;
  }

  if (
    isEmbedRoute === false &&
    isAuthenticated &&
    (psaStatusLoading || psaStatus === null || !isMspInfoLoaded || !flagsLoaded)
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: NEUTRAL.page,
        }}
      >
        <Typography sx={{ fontFamily: "var(--theo-font-sans)" }}>Loading...</Typography>
      </Box>
    );
  }

  // Embed layout: no sidebar, topbar, or app chrome (e.g. ConnectWise Hosted API Pod)
  if (isEmbedRoute) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100%",
          overflow: "hidden",
          background: NEUTRAL.page,
        }}
      >
        <Routes>
          <Route path="/embed/connectwise-pod" element={<ConnectWisePodEmbed />} />
        </Routes>
      </Box>
    );
  }

  // Ticket popup (LD `service-desk-inbox`): chromeless full window, no sidebar / topbar
  if (
    serviceDeskInboxEnabled &&
    isSupervisorTicketChromelessPath(location.pathname) &&
    !isPasswordRoute &&
    !shouldShowOnboarding &&
    !shouldShowPsaSetup
  ) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100%",
          overflow: "hidden",
          bgcolor: NEUTRAL.page,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Routes>
          <Route path="/supervisor/tickets/:ticketId" element={<SupervisorTicketDetail />} />
        </Routes>
      </Box>
    );
  }

  return (
    <ConfigurationSupportProvider>
    <Box
      className="theo-app-shell-root"
      sx={{
        display: "flex",
        height: "100vh",
        background: NEUTRAL.page,
        overflow: "hidden",
      }}
    >
      {!isPasswordRoute && (
        <Box className="theo-app-shell-sidebar" sx={{ flexShrink: 0 }}>
          <Sidebar
            isSidebarExpanded={isSidebarExpanded}
            onToggleExpanded={toggleSidebar}
            handleDrawerToggle={handleDrawerToggle}
            mobileOpen={mobileOpen}
            showPsaSetupOnly={shouldShowPsaSetup}
            autotaskCategories={ticketScopeCategories}
          />
        </Box>
      )}
      <Box className="theo-app-shell-content" sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {!isPasswordRoute && (
          <Box
            className="theo-app-shell-topbar"
            sx={{
              position: "sticky",
              zIndex: 1100,
              top: 0,
              right: 0,
              left: {
                xs: 0,
                sm: isSidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
              },
            }}
          >
              <Topbar pageTitle={getPageTitle(location.pathname)} notificationsEnabled={canAccessSchedules || (location.pathname === "/billing" && canShowBilling)} />
          </Box>
        )}
        <Box
          className="theo-app-shell-main"
          sx={{
            flex: 1,
            p: 0,
            display: "flex",
            overflow: "auto",
            minWidth: 0,
            minHeight: 0,
            position: "relative",
          }}
        >
          {shouldShowOnboarding ? (
            <OnboardingWizard 
              mspId={userMspInfo?.msp_id || 0} 
              onComplete={async () => {
                await refetchOnboarding();
                window.location.href = HOME_PATH;
              }} 
            />
          ) : shouldShowPsaSetup ? (
            <PsaSetupScreen />
          ) : (
            <Routes>
            <Route path="/" element={<Navigate to={HOME_PATH} replace />} />
            <Route path="/dashboard" element={<Navigate to={HOME_PATH} replace />} />
            <Route
              path="/supervisor/tickets/:ticketId"
              element={<SupervisorTicketDetail />}
            />
            <Route
              path="/supervisor/:slug"
              element={
                <SectionFrame nav={<WorkScopeNav categories={ticketScopeCategories} />}>
                  <SupervisorQueueRoute
                    technicianGroupList={Array.isArray(technicianGroupList) ? technicianGroupList : []}
                    ninjaoneBoardList={Array.isArray(ninjaoneBoardList) ? ninjaoneBoardList : []}
                    mspType={userMspInfo?.msp_type}
                  />
                </SectionFrame>
              }
            />
            {ticketScopeCategories.map((category) => (
              <Route
                key={category.groupId}
                path={`/supervisor/categories/${slugify(category.name)}`}
                element={
                  <SectionFrame nav={<WorkScopeNav categories={ticketScopeCategories} />}>
                    <GroupQueue categoryName={category.name} categoryId={Number(category.groupId)} />
                  </SectionFrame>
                }
              />
            ))}
            <Route path="/chat-assistant" element={<ChatAssistant />} />
            <Route path="/chat-assistant/:threadId" element={<ChatAssistant />} />
            <Route path="/devices/:sourceType/:sourceId/:deviceId" element={<RmmDeviceDetail />} />
            <Route path="/devices/:sourceType/:sourceId/:deviceId/:threadId" element={<RmmDeviceDetail />} />
            <Route
              path="/deep-research"
              element={
                canAccessAnalyticsAssistant ? (
                  <ChatAssistant surface="deepResearch" />
                ) : (
                  <Navigate to="/chat-assistant" replace />
                )
              }
            />
            <Route
              path="/deep-research/:threadId"
              element={
                canAccessAnalyticsAssistant ? (
                  <ChatAssistant surface="deepResearch" />
                ) : (
                  <RedirectDeepResearchThread />
                )
              }
            />
            <Route path="/export_results" element={<ExportResults />} />
            <Route path="/csv-export" element={<CsvExportDownload />} />
            <Route path="/pdf-export" element={<PdfExportDownload />} />
            <Route path="/knowledge-base" element={<KnowledgeBase />} />
            <Route path="/guides/new" element={<GuideFormPage />} />
            <Route path="/guides/:id/edit" element={<GuideFormPage />} />
            <Route path="/guides/:id" element={<GuideViewPage />} />
            <Route path="/guides" element={<Guides />} />
            <Route path="/docs/new" element={<DocFormPage />} />
            <Route path="/docs/:id/edit" element={<DocFormPage />} />
            <Route path="/docs/:id" element={<DocViewPage />} />
            <Route path="/docs" element={<Docs />} />
            <Route
              path="/custom_workflows/new"
              element={
                <CustomWorkflowsRoute>
                  <CustomWorkflowForm />
                </CustomWorkflowsRoute>
              }
            />
            <Route
              path="/custom_workflows/:id/edit"
              element={
                <CustomWorkflowsRoute>
                  <CustomWorkflowForm />
                </CustomWorkflowsRoute>
              }
            />
            <Route
              path="/custom_workflows"
              element={
                <CustomWorkflowsRoute>
                  <CustomWorkflows />
                </CustomWorkflowsRoute>
              }
            />
            <Route
              path="/schedules"
              element={canAccessSchedules ? <Schedules /> : <Navigate to={HOME_PATH} replace />}
            />
            <Route path="/accounts" element={<Customers />} />
            <Route path="/theo-users" element={<TheoUsers />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/application-skills" element={<Navigate to="/playbooks" replace />} />
            <Route
              path="/playbooks"
              element={canAccessPlaybooks ? <ApplicationPlaybooks /> : <Navigate to={HOME_PATH} replace />}
            />
            <Route path="/help-center" element={<Navigate to="/help-center/guides" replace />} />
            <Route
              path="/agents/new"
              element={
                canCreateAgent ? (
                  <CreateAgent />
                ) : (
                  <Navigate to="/theo-automations/monitor" replace />
                )
              }
            />
            <Route path="/agents/:agentId/:panel" element={<AgentPlaceholder />} />
            <Route path="/theo-automations/monitor" element={<TheoAutomationsMonitor />} />
            <Route
              path="/theo-overview"
              // Never back to HOME_PATH: this is home, so a closed gate needs somewhere else to land.
              element={showTheoAnalytics ? <TheoOverview /> : <Navigate to="/chat-assistant" replace />}
            />
            <Route
              path="/theo-automations/configure"
              element={<AgentConfigure slug={DEFAULT_AGENT_SLUG} />}
            />
            <Route
              path="/ai-automations"
              element={<Navigate to="/theo-automations/configure" replace />}
            />
            <Route path="/help-center/documentation" element={<TheoDocumentation />} />
            <Route path="/help-center/release-notes" element={<ReleaseNotes />} />
            <Route path="/help-center/guides/:slug" element={<HelpCenterGuideArticle />} />
            <Route path="/help-center/guides" element={<HelpCenterGuidesHub />} />
            <Route path="/documentation" element={<Navigate to="/help-center/documentation" replace />} />
            <Route
              path="/app-configuration/:customerId/:appName"
              element={<AppConfiguration />}
            />
            <Route
              path="/platform-configuration/:customerId/:appName"
              element={<PlatformConfiguration />}
            />
            <Route
              path="/supervisor"
              element={
                <SectionFrame nav={<WorkScopeNav categories={ticketScopeCategories} />}>
                  <Supervisor />
                </SectionFrame>
              }
            />
            <Route path="/neo-analytics" element={<NeoAnalytics />} />
            <Route
              path="/queue-analytics"
              element={showTheoAnalytics ? <QueueAnalytics /> : <Navigate to="/neo-analytics" replace />}
            />
            <Route
              path="/msp-analytics"
              element={<MspAnalytics />}
            />
            <Route
              path="/research-reports"
              element={canAccessAnalyticsAssistant ? <ResearchReports /> : <Navigate to={HOME_PATH} replace />}
            />
            <Route
              path="/report/:jobId"
              element={canAccessAnalyticsAssistant ? <ResearchReportView /> : <Navigate to={HOME_PATH} replace />}
            />
            <Route path="/usage" element={<Usage />} />
            <Route path="/billing" element={canShowBilling ? <Billing /> : <Navigate to={HOME_PATH} replace />} />
            <Route path="/audit-logs" element={<AuditLogs />} />
            <Route path="/neo-ai" element={<TheoAI />} />
            <Route path="/configuration" element={<Configuration />} />
            <Route path="/observer" element={
              <ProtectedObserverRoute>
                <UserThreadObserver />
              </ProtectedObserverRoute>
            } />
            <Route path="/observer/change-log" element={
              <ProtectedObserverRoute>
                <ConfigChangeLog />
              </ProtectedObserverRoute>
            } />
            <Route path="/password" element={<Password />} />
            <Route path="/:ticketId" element={<ShortTicketLinkRoute />} />
            </Routes>
          )}
        </Box>
      </Box>
      {/* LiveDebugPanel portals in here so it pushes the page over instead of covering it. */}
      <Box id={LIVE_DEBUG_SLOT_ID} className="theo-app-shell-live-debug" sx={{ display: "flex", flexShrink: 0, height: "100%" }} />
    </Box>
    <ConfigurationSupportChat />
    </ConfigurationSupportProvider>
  );
};

export default App;
